import { useState, useCallback, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { surveillanceReportApi } from '../api/api';
import type { AlertsResponse } from '../types/types';

export const useSurveillanceDashboard = () => {
  const { clientName, projectName } = useParams<{ clientName: string; projectName: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [data, setData] = useState<AlertsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get filter values from URL params
  const searchQuery = searchParams.get('q') || '';
  const dateFilter = searchParams.get('date') || '';
  const resultFilter = searchParams.get('result') || '';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  // Load alerts
  const loadAlerts = useCallback(async () => {
    if (!clientName || !projectName) {
      setError('Missing client or project name');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await surveillanceReportApi.getAlerts(
        clientName,
        projectName,
        {
          search: searchQuery || undefined,
          date: dateFilter || undefined,
          result: resultFilter || undefined,
          page: currentPage,
          page_size: 20,
        }
      );
      setData(response);
    } catch (err: any) {
      console.error('Error loading alerts:', err);
      setError(err.message || 'Failed to load alerts');
    } finally {
      setIsLoading(false);
    }
  }, [clientName, projectName, searchQuery, dateFilter, resultFilter, currentPage]);

  // Load alerts when params change
  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  // Handle search
  const handleSearch = useCallback((query: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (query) {
      newParams.set('q', query);
    } else {
      newParams.delete('q');
    }
    newParams.delete('page'); // Reset to page 1
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Handle date filter
  const handleDateFilter = useCallback((date: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (date) {
      newParams.set('date', date);
    } else {
      newParams.delete('date');
    }
    newParams.delete('page'); // Reset to page 1
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Handle result filter
  const handleResultFilter = useCallback((result: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (result) {
      newParams.set('result', result);
    } else {
      newParams.delete('result');
    }
    newParams.delete('page'); // Reset to page 1
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Handle pagination
  const handlePageChange = useCallback((page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(page));
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  return {
    data,
    isLoading,
    error,
    searchQuery,
    dateFilter,
    resultFilter,
    currentPage,
    handleSearch,
    handleDateFilter,
    handleResultFilter,
    handlePageChange,
    clearFilters,
    refetch: loadAlerts,
  };
};






